import React from "react";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import { SupplySide } from "api/Dashboard";
import { Panel, EmptyState, currency } from "./shared";

/**
 * The supply side - what we owe and to whom. Invisible everywhere in the
 * app before this (spec 3.3).
 *
 * COMMITTED IS WINDOWED AND THE TAIL IS NAMED SEPARATELY. A PO with no
 * provider invoice against it is only "committed" if the provider
 * invoice flow was in use when it was raised; it was not for most of the
 * history, so the uninvoiced tail is old work billed outside the app,
 * not exposure. One combined number would read as a million dollars of
 * commitment that does not exist. See METRIC_DEFINITIONS.md.
 *
 * "Owed to providers" is absent on purpose: payment status lives in
 * Zoho and no local row carries it. An approximated figure in a money
 * card is worse than no card.
 */

const Stat: React.FC<{
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "warn";
}> = ({ label, value, detail, tone = "neutral" }) => (
  <Box sx={{ flex: "1 1 200px", minWidth: "190px" }}>
    <Box
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "12px",
        color: themevals.colors.dkgrey,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "22px",
        fontWeight: 600,
        color: tone === "warn" ? themevals.colors.warning : themevals.colors.base_800,
        margin: "4px 0",
      }}
    >
      {value}
    </Box>
    <Box
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "12px",
        color: themevals.colors.dkgrey,
        lineHeight: 1.5,
      }}
    >
      {detail}
    </Box>
  </Box>
);

const SupplyBlock: React.FC<{ supply: SupplySide }> = ({ supply }) => {
  const { committed, neverInvoiced, awaitingApproval, variance, concentration } =
    supply;

  const hasAnything =
    committed.count > 0 ||
    neverInvoiced.count > 0 ||
    awaitingApproval.count > 0 ||
    concentration.providers.length > 0;

  return (
    <Panel title="Supply side" sx={{ marginBottom: "24px" }}>
      {!hasAnything ? (
        <EmptyState
          headline="No provider activity yet"
          detail="This block fills as purchase orders go out and providers invoice against them."
        />
      ) : (
        <Box>
          <Flex sx={{ gap: "24px", flexWrap: "wrap", marginBottom: "20px" }}>
            <Stat
              label={"Committed, last " + committed.windowDays + " days"}
              value={currency(committed.value)}
              detail={
                committed.count +
                " purchase orders issued with no provider invoice back yet"
              }
            />
            <Stat
              label="Awaiting approval"
              value={currency(awaitingApproval.value)}
              detail={
                awaitingApproval.count +
                " provider invoices waiting on someone here"
              }
            />
            <Stat
              label="Billed above PO"
              value={currency(variance.value)}
              detail={
                variance.count === 0
                  ? "No provider is billing above their purchase order"
                  : variance.count + " provider invoices exceed their PO"
              }
              tone={variance.count > 0 ? "warn" : "neutral"}
            />
            <Stat
              label="Top provider share"
              value={
                concentration.sharePercent === null
                  ? "n/a"
                  : concentration.sharePercent.toFixed(0) + "%"
              }
              detail={
                concentration.topProviderName
                  ? concentration.topProviderName +
                    ", " +
                    currency(concentration.topProviderSpend) +
                    " of " +
                    currency(concentration.totalSpend) +
                    " top-8 spend"
                  : "No provider spend in the last 12 months"
              }
            />
          </Flex>

          {neverInvoiced.count > 0 && (
            <Box
              sx={{
                padding: "12px",
                borderRadius: "6px",
                backgroundColor: "#F7F9FC",
                fontFamily: "Poppins, sans-serif",
                marginBottom: "16px",
              }}
            >
              <Box sx={{ fontSize: "12px", fontWeight: 600, color: "base_800" }}>
                {neverInvoiced.count +
                  " older purchase orders (" +
                  currency(neverInvoiced.value) +
                  ") have no provider invoice in the app"}
              </Box>
              <Box
                sx={{
                  fontSize: "12px",
                  color: themevals.colors.dkgrey,
                  marginTop: "4px",
                  lineHeight: 1.5,
                }}
              >
                Deliberately kept out of the committed figure. Most predate the
                provider invoice flow and were billed outside the app, so this
                is a reconciliation gap rather than money owed.
              </Box>
            </Box>
          )}

          {concentration.providers.length > 0 && (
            <Box>
              {concentration.providers.map((p) => {
                const share =
                  concentration.totalSpend > 0
                    ? Math.round((p.spend / concentration.totalSpend) * 100)
                    : 0;
                return (
                  <Flex
                    key={p.name}
                    sx={{
                      alignItems: "center",
                      gap: "12px",
                      padding: "6px 0",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                    }}
                  >
                    <Box sx={{ width: "160px", flexShrink: 0, color: "base_800" }}>
                      {p.name}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        height: "6px",
                        borderRadius: "3px",
                        backgroundColor: "#F1F1F1",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: share + "%",
                          backgroundColor: themevals.colors.info,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: "150px",
                        textAlign: "right",
                        color: themevals.colors.dkgrey,
                        flexShrink: 0,
                      }}
                    >
                      {currency(p.spend) + " / " + p.purchaseOrders + " POs"}
                    </Box>
                  </Flex>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Panel>
  );
};

export default SupplyBlock;
